import { useCallback, useState } from 'react'
import { Link, useLocation } from 'react-router-dom-v5-compat'

import { addMinutes, isAfter, isPast } from 'date-fns'

import {
  AlphaBadge,
  Button,
  CheckIcon,
  CloseIcon,
  CommentSquareQuoteIcon,
  CopyPlusIcon,
  DropdownMenu,
  ExternalLinkIcon,
  EyeIcon,
  Flex,
  IconButton,
  LinkIcon,
  MoveFileIcon,
  PencilIcon,
  Popover,
  QueueBottomIcon,
  SendClockIcon,
  SendIcon,
  ShareIcon,
  Text,
  toast,
  Tooltip,
  UserCheckIcon,
} from '@buffer-mono/popcorn'
import type { PopoverProps } from '@buffer-mono/popcorn'

import { SplitProtected } from '~publish/components/SplitProtected'
import { formatTimeZone } from '~publish/helpers/dateFormatters'
import { omitFields } from '~publish/helpers/object'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import { useTimezone } from '~publish/hooks/useTimezone'
import type { RpcUpdate } from '~publish/legacy/post/types'
import callRpc from '~publish/legacy/utils/call-rpc'

import { getEngageUrl } from '../helpers'
import { usePostData, useTrackActionClicked } from '../PostCardContext'
import { useAddPostToQueue } from '../useAddPostToQueue'
import { useApprovePost } from '../useApprovePost'
import { useMovePostToDrafts } from '../useMovePostToDrafts'
import { usePublishPostNow } from '../usePublishPostNow'
import { useRejectPost } from '../useRejectPost'
import { useRequestPostApproval } from '../useRequestApproval'
import { useRevertPostApprovalRequest } from '../useRevertPostApprovalRequest'
import { DeletePostMenuItem } from './DeletePostMenuItem'
import { MoreActionsDropdown } from './MoreActionsDropdown'

import styles from './PostCardActions.module.css'

const omittedFields: (keyof RpcUpdate)[] = [
  'id',
  'due_at',
  'day',
  'scheduled_at',
  'scheduledAt',
  'serviceUpdateId',
  'createdAt',
  'statistics',
  'status',
  'serviceLink',
]

type OmittedField = (typeof omittedFields)[number]

type RpcUpdateWithoutOmittedFields = Omit<RpcUpdate, OmittedField>

const omitDuplicatedFields = (
  post: RpcUpdate,
): RpcUpdateWithoutOmittedFields => {
  const newPost = omitFields(post, omittedFields)

  return {
    ...newPost,
    isSent: false,
    isPastDue: false,
    isDraft: false,
  }
}

const ScheduleConfirmationPopover = ({
  children,
  ...props
}: PopoverProps): JSX.Element | null => {
  const { dueAt, id } = usePostData()
  const [addPostToQueue, { loading }] = useAddPostToQueue()
  const trackActionClicked = useTrackActionClicked()
  const dateTimeFormatter = useDateTimeFormatter()
  const timeZone = useTimezone()
  const { triggerAttributes, editPostInComposer } = usePostComposer()

  const handleAddToQueue = useCallback(async () => {
    const { success, error } = await addPostToQueue({ variables: { id } })
    trackActionClicked('addToQueue-1')

    if (!success) {
      toast.error(`Failed to schedule post: ${error.message}`)
      return
    }

    toast.success(`Post scheduled`)
  }, [addPostToQueue, trackActionClicked, id])

  if (!dueAt) return null

  const formattedDate = dateTimeFormatter(dueAt, {
    dateFormat: "MMMM d 'at'",
    includeTimeZone: false,
  })

  return (
    <Popover {...props}>
      <Popover.Trigger>{children}</Popover.Trigger>
      <Popover.Content align="end">
        <Flex direction="column" gap="xs">
          <Text>Your post will be published on:</Text>
          <div className={styles.schedulePreview}>
            <Flex gap="xs" align="center">
              <SendClockIcon className={styles.icon} />
              <Text weight="medium">{formattedDate}</Text>
            </Flex>
            <Text size="sm" className={styles.timezone}>
              {formatTimeZone(timeZone)}
            </Text>
          </div>
        </Flex>
        <div className={styles.separator} />
        <Flex justify="end" gap="xs">
          <Button
            {...triggerAttributes}
            variant="secondary"
            onClick={(): Promise<void> =>
              editPostInComposer({
                postId: id,
                cta: 'publish-allChannels-postCard-editPost-1',
              })
            }
          >
            Change
          </Button>
          <Button
            variant="primary"
            loading={loading}
            onClick={handleAddToQueue}
          >
            Schedule Post
          </Button>
        </Flex>
      </Popover.Content>
    </Popover>
  )
}

const CopyLinkMenuItem = (): JSX.Element | null => {
  const { externalLink, allowedActions } = usePostData()
  const trackActionClicked = useTrackActionClicked()

  const handleCopyLink = useCallback(async () => {
    if (!externalLink) {
      return
    }

    await navigator.clipboard.writeText(externalLink)
    trackActionClicked('copyLink-1')
    toast.success('Link copied to the clipboard')
  }, [externalLink, trackActionClicked])

  if (!externalLink || !allowedActions.includes('copyPostLink')) {
    return null
  }

  return (
    <DropdownMenu.Item onSelect={handleCopyLink}>
      <LinkIcon />
      Copy link
    </DropdownMenu.Item>
  )
}

const ShareLinkMenuItem = (): JSX.Element | null => {
  const { externalLink, allowedActions } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const { triggerAttributes, createNewPostInComposer } = usePostComposer()

  if (!externalLink || !allowedActions.includes('sharePostLink')) {
    return null
  }

  return (
    <DropdownMenu.Item
      {...triggerAttributes}
      onSelect={(): Promise<void> => {
        trackActionClicked('shareLink-1')
        return createNewPostInComposer({
          cta: 'publish-allChannels-postCard-shareLink-1',
          prefillPostData: { text: externalLink },
        })
      }}
    >
      <ShareIcon />
      Share Link in a Post
    </DropdownMenu.Item>
  )
}

const PublishNowMenuItem = (): JSX.Element | null => {
  const { id, channel, allowedActions, status, sharedNow } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const shouldDisablePublishNow = status === 'sending' || sharedNow
  const [publishPostNow, { loading }] = usePublishPostNow()

  const handlePublishNow = useCallback(
    async (e: Event) => {
      e.preventDefault()
      const { success, error, data } = await publishPostNow({
        variables: { id },
      })
      trackActionClicked('publishNow-1')

      if (!success) {
        toast.error(`Post publishing failed: ${error.message}`)
        return
      }

      const message =
        data?.post.status === 'sent'
          ? `Post published to ${channel.name}`
          : `Preparing your post to publish to ${channel.name}. It will go live shortly`

      toast.success(message)
    },
    [publishPostNow, id, channel.name, trackActionClicked],
  )

  if (!allowedActions.includes('publishPostNow')) {
    return null
  }

  return (
    <DropdownMenu.Item
      loading={loading}
      onSelect={handlePublishNow}
      disabled={shouldDisablePublishNow}
    >
      <SendIcon />
      Publish Now
    </DropdownMenu.Item>
  )
}

const AddToQueueMenuItem = (): JSX.Element | null => {
  const { id, allowedActions, status } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const [addPostToQueue, { loading }] = useAddPostToQueue()

  const handleAddToQueue = useCallback(
    async (e: Event) => {
      e.preventDefault()
      const { success, error } = await addPostToQueue({ variables: { id } })
      trackActionClicked('addToQueue-1')

      if (!success) {
        toast.error(`Failed to add post to queue: ${error.message}`)
        return
      }

      toast.success(`Post added to queue`)
    },
    [addPostToQueue, trackActionClicked, id],
  )

  if (
    !allowedActions.includes('addPostToQueue') ||
    status === 'needs_approval'
  ) {
    return null
  }

  return (
    <DropdownMenu.Item loading={loading} onSelect={handleAddToQueue}>
      <QueueBottomIcon />
      Add to Queue
    </DropdownMenu.Item>
  )
}

const EditPostButton = (): JSX.Element | null => {
  const { allowedActions, id } = usePostData()
  const { triggerAttributes, editPostInComposer } = usePostComposer()

  if (!allowedActions.includes('updatePost')) {
    return null
  }

  return (
    <IconButton
      {...triggerAttributes}
      variant="secondary"
      label="Edit"
      tooltip="Edit"
      onClick={(): Promise<void> =>
        editPostInComposer({
          postId: id,
          cta: 'publish-allChannels-postCard-editPost-1',
        })
      }
    >
      <PencilIcon />
    </IconButton>
  )
}

const ViewPostMenuItem = (): JSX.Element | null => {
  const post = usePostData()
  const { search } = useLocation()

  const url = `posts/${post.id}${search}`

  return (
    <DropdownMenu.Item asChild>
      <Link to={url} aria-haspopup="dialog" aria-expanded="false">
        <EyeIcon />
        View Post
        <AlphaBadge size="small" />
      </Link>
    </DropdownMenu.Item>
  )
}

const DuplicateMenuItem = (): JSX.Element | null => {
  const [loading, setLoading] = useState(false)
  const { id, via, assets, channel } = usePostData()
  const { triggerAttributes, duplicatePostInComposer } = usePostComposer()
  const trackActionClicked = useTrackActionClicked()
  const cta = 'publish-allChannels-postCard-duplicatePost-1'

  // Ingested posts only fetch thumbnails, so we do not have the media files available to duplicate
  const isDuplicateUnavailable = via === 'network' && assets.length > 0

  const handleDuplicatePost = useCallback(async () => {
    setLoading(true)

    try {
      const post = await callRpc('getPost', { updateId: id })
      const prefillPostData = omitDuplicatedFields(post) as RpcUpdate

      duplicatePostInComposer({
        cta,
        channels: [channel.id],
        prefillPostData,
        duplicatedFrom: id,
      })
    } catch (err: unknown) {
      const error = err as Error
      toast.error(`Failed to duplicate post: ${error.message}`)
    } finally {
      setLoading(false)
      trackActionClicked('duplicate-1')
    }
  }, [id, duplicatePostInComposer, trackActionClicked, channel.id])

  if (isDuplicateUnavailable) {
    return (
      <Tooltip
        content="We can only duplicate posts shared via Buffer"
        side="right"
        sideOffset={4}
      >
        <DropdownMenu.Item disabled>
          <CopyPlusIcon />
          Duplicate
        </DropdownMenu.Item>
      </Tooltip>
    )
  }

  return (
    <DropdownMenu.Item
      {...triggerAttributes}
      loading={loading}
      onClick={handleDuplicatePost}
    >
      <CopyPlusIcon />
      Duplicate
    </DropdownMenu.Item>
  )
}

const MoveToDraftsMenuItem = (): JSX.Element | null => {
  const { id, allowedActions } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const [movePostToDrafts, { loading }] = useMovePostToDrafts()

  const handleMoveToDrafts = useCallback(
    async (e: Event) => {
      e.preventDefault()
      const { data, error } = await movePostToDrafts({ variables: { id } })
      trackActionClicked('moveToDrafts-1')

      if (data) {
        toast.success('Post moved to drafts')
      }

      if (error) {
        toast.error(`Failed to move post to drafts: ${error.message}`)
      }
    },
    [movePostToDrafts, trackActionClicked, id],
  )

  if (!allowedActions.includes('movePostToDraft')) {
    return null
  }

  return (
    <DropdownMenu.Item loading={loading} onSelect={handleMoveToDrafts}>
      <MoveFileIcon />
      Move to Drafts
    </DropdownMenu.Item>
  )
}

const AddToQueueButton = (): JSX.Element | null => {
  const { id, allowedActions } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const [addPostToQueue, { loading }] = useAddPostToQueue()

  const handleAddToQueue = useCallback(async () => {
    const { success, error } = await addPostToQueue({ variables: { id } })
    trackActionClicked('addToQueue-1')

    if (!success) {
      toast.error(`Failed to add post to queue: ${error.message}`)
      return
    }

    toast.success(`Post added to queue`)
  }, [addPostToQueue, trackActionClicked, id])

  if (!allowedActions.includes('addPostToQueue')) {
    return null
  }

  return (
    <Button
      data-testid="add-to-queue-button"
      variant="secondary"
      loading={loading}
      onClick={handleAddToQueue}
    >
      <QueueBottomIcon /> Add to Queue
    </Button>
  )
}

const PublishNowButton = (): JSX.Element | null => {
  const { id, channel, allowedActions, status, sharedNow, dueAt } =
    usePostData()
  const [publishPostNow, { loading }] = usePublishPostNow()
  const trackActionClicked = useTrackActionClicked()

  // Disable publish now if the post is past due and within 60 minutes
  // or if the post is in a sending state
  // TODO: Refactor this to use a realtime update that checks the posts status
  // currently we do not have a realtime update for the post status
  const isPastDue = dueAt && isPast(dueAt)
  const isOverdueWithinHour =
    dueAt && isPastDue && isAfter(addMinutes(new Date(dueAt), 60), new Date())
  const shouldDisablePublishNow =
    status === 'sending' ||
    sharedNow ||
    Boolean(isPastDue && isOverdueWithinHour)

  const handlePublishNow = useCallback(async () => {
    const { success, error, data } = await publishPostNow({ variables: { id } })
    trackActionClicked('publishNow-1')

    if (!success) {
      toast.error(`Post publishing failed: ${error.message}`)
      return
    }

    const message =
      data?.post.status === 'sent'
        ? `Post published to ${channel.name}`
        : `Preparing your post to publish to ${channel.name}. It will go live shortly`

    toast.success(message)
  }, [id, publishPostNow, trackActionClicked, channel.name])

  if (!allowedActions.includes('publishPostNow')) {
    return null
  }

  if (shouldDisablePublishNow) {
    return (
      <Tooltip
        content="Some media posts may need a moment to process and publish."
        side="top"
        sideOffset={4}
      >
        <Button variant="secondary" disabled>
          <SendIcon /> Publish Now
        </Button>
      </Tooltip>
    )
  }

  return (
    <Button
      variant="secondary"
      loading={loading}
      onClick={handlePublishNow}
      disabled={shouldDisablePublishNow}
    >
      <SendIcon /> Publish Now
    </Button>
  )
}

const RetryNowButton = (): JSX.Element | null => {
  const { id, channel, allowedActions, status, sharedNow } = usePostData()
  const [publishPostNow, { loading }] = usePublishPostNow()
  const shouldDisablePublishNow = status === 'sending' || sharedNow
  const trackActionClicked = useTrackActionClicked()

  const handleRetryNow = useCallback(async () => {
    const { success, error, data } = await publishPostNow({ variables: { id } })
    trackActionClicked('retryNow-1')

    if (!success) {
      toast.error(`Post publishing failed: ${error.message}`)
      return
    }

    const message =
      data?.post.status === 'sent'
        ? `Post published to ${channel.name}`
        : `Preparing your post to publish to ${channel.name}. It will go live shortly`

    toast.success(message)
  }, [id, publishPostNow, trackActionClicked, channel.name])

  if (!allowedActions.includes('publishPostNow')) {
    return null
  }

  return (
    <Button
      variant="secondary"
      loading={loading}
      onClick={handleRetryNow}
      disabled={shouldDisablePublishNow}
    >
      <SendIcon /> Retry Now
    </Button>
  )
}

const SchedulePostButton = (): JSX.Element | null => {
  const { allowedActions } = usePostData()
  const trackActionClicked = useTrackActionClicked()

  if (!allowedActions.includes('addPostToQueue')) {
    return null
  }

  return (
    <ScheduleConfirmationPopover>
      <Button
        variant="secondary"
        onClick={(): void => trackActionClicked('schedulePost-1')}
      >
        <SendClockIcon /> Schedule Post
      </Button>
    </ScheduleConfirmationPopover>
  )
}

const ReschedulePostButton = (): JSX.Element | null => {
  const { allowedActions, id } = usePostData()
  const { triggerAttributes, editPostInComposer } = usePostComposer()
  const trackActionClicked = useTrackActionClicked()

  if (!allowedActions.includes('updatePost')) {
    return null
  }

  return (
    <Button
      {...triggerAttributes}
      variant="secondary"
      onClick={(): Promise<void> => {
        trackActionClicked('reschedule-1')
        return editPostInComposer({
          postId: id,
          cta: 'publish-allChannels-postCard-editPost-1',
        })
      }}
    >
      <SendClockIcon /> Reschedule
    </Button>
  )
}

const RequestApprovalButton = (): JSX.Element | null => {
  const { id, allowedActions } = usePostData()
  const [requestApproval, { loading }] = useRequestPostApproval()
  const trackActionClicked = useTrackActionClicked()

  const handleRequestApproval = useCallback(async () => {
    const { error, success } = await requestApproval({
      variables: { id },
    })
    trackActionClicked('requestApproval-1')

    if (!success) {
      toast.error(`Failed to request approval: ${error.message}`)
      return
    }

    toast.success('Approval requested')
  }, [requestApproval, trackActionClicked, id])

  if (!allowedActions.includes('requestPostApproval')) {
    return null
  }

  return (
    <Button variant="primary" loading={loading} onClick={handleRequestApproval}>
      <UserCheckIcon /> Request Approval
    </Button>
  )
}

const RevertApprovalRequestButton = (): JSX.Element | null => {
  const { id, allowedActions } = usePostData()
  const [revertApprovalRequest, { loading }] = useRevertPostApprovalRequest()
  const trackActionClicked = useTrackActionClicked()

  const handleRevertApprovalRequest = useCallback(async () => {
    const { error, success } = await revertApprovalRequest({
      variables: { id },
    })
    trackActionClicked('revertApprovalRequest-1')

    if (!success) {
      toast.error(`Failed to revert approval request. ${error.message}`)
      return
    }

    toast.success('Post moved to back to drafts')
  }, [revertApprovalRequest, trackActionClicked, id])

  if (!allowedActions.includes('revertPostApprovalRequest')) {
    return null
  }

  return (
    <Button
      variant="secondary"
      loading={loading}
      onClick={handleRevertApprovalRequest}
    >
      <CloseIcon /> Revert Approval Request
    </Button>
  )
}

const ApprovePostButton = (): JSX.Element | null => {
  const { id, allowedActions, isCustomScheduled } = usePostData()
  const [approvePost, { loading }] = useApprovePost()
  const cta = isCustomScheduled ? 'Schedule' : 'AddToQueue'
  const trackActionClicked = useTrackActionClicked()

  const handleApprovePost = useCallback(async () => {
    const { error, success } = await approvePost({
      variables: { id },
    })
    trackActionClicked(`approveAnd${cta}-1`)

    if (!success) {
      toast.error(`Failed to approve post. ${error.message}`)
      return
    }

    toast.success('Post approved')
  }, [approvePost, trackActionClicked, cta, id])

  if (!allowedActions.includes('approvePost')) {
    return null
  }

  const label = isCustomScheduled ? 'Schedule Post' : 'Add to Queue'

  return (
    <Button variant="success" loading={loading} onClick={handleApprovePost}>
      <CheckIcon /> {label}
    </Button>
  )
}

const RejectPostButton = (): JSX.Element | null => {
  const { id, allowedActions } = usePostData()
  const [rejectPost, { loading }] = useRejectPost()
  const trackActionClicked = useTrackActionClicked()

  const handleRejectPost = useCallback(async () => {
    const { error, success } = await rejectPost({
      variables: { id },
    })
    trackActionClicked('reject-1')

    if (!success) {
      toast.error(`Failed to reject post. ${error.message}`)
      return
    }

    toast.success('Post rejected and moved back to drafts')
  }, [rejectPost, trackActionClicked, id])

  if (!allowedActions.includes('rejectPost')) {
    return null
  }

  return (
    <Button variant="critical" loading={loading} onClick={handleRejectPost}>
      <CloseIcon /> Reject
    </Button>
  )
}

const ViewPostButton = (): JSX.Element | null => {
  const { externalLink, allowedActions } = usePostData()
  const trackActionClicked = useTrackActionClicked()

  if (!externalLink || !allowedActions.includes('viewPost')) {
    return null
  }

  return (
    <Button
      as="a"
      href={externalLink}
      target="_blank"
      rel="noopener noreferrer"
      variant="secondary"
      onClick={(): void => trackActionClicked('viewPost-1')}
    >
      <ExternalLinkIcon /> View Post
    </Button>
  )
}

const ViewCommentsMenuItem = (): JSX.Element | null => {
  const { status, channel, metrics } = usePostData()
  const trackActionClicked = useTrackActionClicked()
  const hasComments = metrics?.find(
    ({ type, value }) => type === 'comments' && value > 0,
  )

  const engageUrl = getEngageUrl(channel)

  if (status !== 'sent' || !hasComments || !engageUrl) {
    return null
  }

  return (
    <DropdownMenu.Item asChild>
      <a
        href={engageUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(): void => trackActionClicked('viewComments-1')}
      >
        <CommentSquareQuoteIcon />
        View Comments
      </a>
    </DropdownMenu.Item>
  )
}

export const PostCardActions = (): JSX.Element | null => {
  const { status, dueAt, isCustomScheduled } = usePostData()

  if (status === 'draft') {
    const isOverDue = dueAt && isPast(dueAt)
    const shouldAddToQueue = !isOverDue && !isCustomScheduled
    return (
      <div className={styles.wrapper}>
        <RequestApprovalButton />
        {isOverDue && <ReschedulePostButton />}
        {isCustomScheduled && !isOverDue && <SchedulePostButton />}
        {shouldAddToQueue && <AddToQueueButton />}
        <EditPostButton />
        <MoreActionsDropdown>
          <MoreActionsDropdown.Group>
            <PublishNowMenuItem />
            <DuplicateMenuItem />
            <SplitProtected name="post-deep-linking">
              <ViewPostMenuItem />
            </SplitProtected>
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <DeletePostMenuItem />
          </MoreActionsDropdown.Group>
        </MoreActionsDropdown>
      </div>
    )
  }

  if (status === 'needs_approval') {
    return (
      <div className={styles.wrapper}>
        <ApprovePostButton />
        <RejectPostButton />
        <RevertApprovalRequestButton />
        <EditPostButton />
        <MoreActionsDropdown>
          <MoreActionsDropdown.Group>
            <PublishNowMenuItem />
            <AddToQueueMenuItem />
            <DuplicateMenuItem />
            <SplitProtected name="post-deep-linking">
              <ViewPostMenuItem />
            </SplitProtected>
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <DeletePostMenuItem />
          </MoreActionsDropdown.Group>
        </MoreActionsDropdown>
      </div>
    )
  }

  if (status === 'scheduled') {
    return (
      <div className={styles.wrapper}>
        <PublishNowButton />
        <EditPostButton />
        <MoreActionsDropdown>
          <MoreActionsDropdown.Group>
            <MoveToDraftsMenuItem />
            <DuplicateMenuItem />
            <SplitProtected name="post-deep-linking">
              <ViewPostMenuItem />
            </SplitProtected>
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <DeletePostMenuItem />
          </MoreActionsDropdown.Group>
        </MoreActionsDropdown>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className={styles.wrapper}>
        <RetryNowButton />
        <EditPostButton />
        <MoreActionsDropdown>
          <MoreActionsDropdown.Group>
            <AddToQueueMenuItem />
            <DuplicateMenuItem />
            <SplitProtected name="post-deep-linking">
              <ViewPostMenuItem />
            </SplitProtected>
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <DeletePostMenuItem />
          </MoreActionsDropdown.Group>
        </MoreActionsDropdown>
      </div>
    )
  }

  if (status === 'sent') {
    return (
      <div className={styles.wrapper}>
        <ViewPostButton />

        <MoreActionsDropdown>
          <MoreActionsDropdown.Group>
            <ShareLinkMenuItem />
            <CopyLinkMenuItem />
            <DuplicateMenuItem />
            <SplitProtected name="post-deep-linking">
              <ViewPostMenuItem />
            </SplitProtected>
          </MoreActionsDropdown.Group>
          <MoreActionsDropdown.Group>
            <ViewCommentsMenuItem />
          </MoreActionsDropdown.Group>
        </MoreActionsDropdown>
      </div>
    )
  }

  return null
}
